import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Pencil, Trash2, Star, Archive } from "lucide-react";

export function useCollection(name) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/${name}`);
            setItems(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [name]); // eslint-disable-line
    const create = async (body) => { const { data } = await api.post(`/${name}`, body); setItems((x) => [data, ...x]); return data; };
    const update = async (id, body) => { const { data } = await api.put(`/${name}/${id}`, body); setItems((x) => x.map((i) => (i.id === id ? data : i))); return data; };
    const remove = async (id) => { await api.delete(`/${name}/${id}`); setItems((x) => x.filter((i) => i.id !== id)); };
    return { items, loading, create, update, remove, reload: load };
}

export function PageHeader({ title, subtitle, action }) {
    return (
        <div className="flex items-end justify-between gap-4 mb-6">
            <div>
                <div className="font-script text-[var(--brand-orange)] text-xl">{subtitle}</div>
                <h1 className="font-serif text-3xl md:text-4xl tracking-tight">{title}</h1>
            </div>
            {action}
        </div>
    );
}

export function CrudActions({ onEdit, onDelete, onArchive, onFavorite, isFav, archived, idBase }) {
    return (
        <div className="flex items-center gap-1.5">
            {onFavorite && (
                <button onClick={onFavorite} data-testid={`${idBase}-favorite`}
                        className={`p-1.5 rounded-full hover:bg-[rgba(255,107,53,0.12)] transition ${isFav ? "text-[var(--brand-orange)]" : "text-[var(--text-ink)]/50"}`}
                        title="Favourite">
                    <Star size={16} fill={isFav ? "currentColor" : "none"} />
                </button>
            )}
            {onEdit && (
                <button onClick={onEdit} data-testid={`${idBase}-edit`}
                        className="p-1.5 rounded-full hover:bg-[rgba(123,45,142,0.12)] text-[var(--text-ink)]/60 transition" title="Edit">
                    <Pencil size={16} />
                </button>
            )}
            {onArchive && (
                <button onClick={onArchive} data-testid={`${idBase}-archive`}
                        className={`p-1.5 rounded-full hover:bg-[rgba(255,179,0,0.18)] transition ${archived ? "text-[var(--brand-orange)]" : "text-[var(--text-ink)]/60"}`} title="Archive">
                    <Archive size={16} />
                </button>
            )}
            {onDelete && (
                <button onClick={onDelete} data-testid={`${idBase}-delete`}
                        className="p-1.5 rounded-full hover:bg-[rgba(229,57,53,0.15)] text-[var(--status-red)] transition" title="Delete">
                    <Trash2 size={16} />
                </button>
            )}
        </div>
    );
}

export function Modal({ open, onClose, title, children, size = "lg" }) {
    if (!open) return null;
    const widthClass = {
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "3xl": "max-w-3xl",
    }[size] || "max-w-lg";
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" onClick={onClose} data-testid="modal-overlay">
            <div onClick={(e) => e.stopPropagation()} className={`card-warm p-6 ${widthClass} w-full max-h-[90vh] overflow-auto scrollbar-thin`}>
                <div className="font-serif text-2xl mb-4">{title}</div>
                {children}
            </div>
        </div>
    );
}

export function Empty({ icon: Icon, title, hint }) {
    return (
        <div className="card-warm p-10 text-center" data-testid="empty-state">
            {Icon && <Icon size={28} className="mx-auto text-[var(--brand-magenta)] mb-2" />}
            <div className="font-serif text-xl">{title}</div>
            <div className="text-sm text-[var(--text-ink)]/65 mt-1">{hint}</div>
        </div>
    );
}

export function StatusDot({ status }) {
    const c = { incomplete: "var(--status-red)", in_progress: "var(--status-amber)",
                complete: "var(--status-green)", appointment: "var(--status-purple)" }[status] || "var(--status-red)";
    return <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: c }} />;
}
